import { Typography } from "antd";
import dayjs from "dayjs";
import { readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { formatCurrency, toApiNumber } from "../../../utils/formatters";
import "./PurchaseOrderPrintSheet.css";

const { Title, Text } = Typography;

const toMoney = (value) => Math.round(toApiNumber(value, 0) * 100) / 100;

function resolveBranding(systemConfig, t) {
  const cfg = systemConfig || readCachedSystemConfig() || {};
  const logo = cfg.invoice_logo_url || cfg.sidebar_logo_url || "";
  return {
    printCompanyTitle:
      (cfg.invoice_company_name || "").trim() ||
      (cfg.site_name || "").trim() ||
      t("poPrintCompanyFallback", "IQ Price"),
    printTagline: (cfg.invoice_tagline || "").trim(),
    printHeaderLogo: logo,
    printWatermarkOn: cfg.invoice_watermark_enabled !== false && Boolean(logo),
    printWmOpacity:
      typeof cfg.invoice_watermark_opacity === "number" ? cfg.invoice_watermark_opacity : 0.07,
    printStampSrc: cfg.invoice_stamp_image_url || "",
    printEmail: (cfg.invoice_email || "").trim(),
    printWeb: (cfg.invoice_website || "").trim(),
    printPhoneLines: (cfg.invoice_phones || "").trim()
      ? cfg.invoice_phones.split(/\r?\n/).map((s) => s.trim()).filter(Boolean)
      : [],
    printAddress: (cfg.invoice_address || "").trim(),
    printFooterNote: (cfg.invoice_footer_note || "").trim(),
  };
}

/**
 * Maps API purchase order → props for PurchaseOrderPrintSheet.
 */
export function buildPrintModelFromApiPo(po, systemConfig, i18n) {
  const mainCode = systemConfig?.currency_code || "IQD";
  const secondaryCode = systemConfig?.secondary_currency_code || "";
  const useDualCurrency = Boolean(secondaryCode);

  const getTranslatedName = (nameInput) => {
    let nameTranslations;
    try {
      nameTranslations = typeof nameInput === "string" ? JSON.parse(nameInput) : nameInput;
    } catch {
      return typeof nameInput === "string" ? nameInput : "—";
    }
    return (
      nameTranslations?.[i18n.language] ||
      nameTranslations?.en ||
      nameTranslations?.ku ||
      nameTranslations?.ar ||
      "—"
    );
  };

  const formatProductLabel = (p) => {
    const name = getTranslatedName(p.name);
    const sku = p.sku || "";
    const bc = p.barcode ? ` · ${p.barcode}` : "";
    return `${sku}${bc} — ${name}`;
  };

  const lines = (po.items || []).map((it, idx) => ({
    key: `k-${it.id ?? idx}`,
    label: it.product ? formatProductLabel(it.product) : `#${it.product_id}`,
    product_id: it.product_id,
    quantity: Number(it.quantity),
    single_price: Number(it.single_price),
  }));

  let cents = 0;
  lines.forEach((row) => {
    cents += Math.round(toApiNumber(row.quantity, 0) * toApiNumber(row.single_price, 0) * 100);
  });
  const lineTotals = cents / 100;

  const mainPaid = toApiNumber(po.amount_paid_main, po.amount_paid ?? 0);
  const sec = toApiNumber(po.amount_paid_secondary, 0);
  const er = po.exchange_rate;
  const secondaryPerMain = er ? toApiNumber(er.main_per_secondary, 0) : 0;
  let appliedMainTotal = toApiNumber(po.amount_paid, 0);
  let selectedRate = null;
  if (useDualCurrency && secondaryPerMain) {
    selectedRate = { main_per_secondary: secondaryPerMain };
    appliedMainTotal = Math.round((mainPaid + sec / secondaryPerMain) * 100) / 100;
  } else if (useDualCurrency) {
    appliedMainTotal = mainPaid;
  }

  const status = po.status || "draft";

  return {
    poId: po.id,
    isPreview: false,
    vendorName: po.vendor?.name ?? "—",
    reference: po.reference || "",
    status,
    poCreatedAt: po.created_at || null,
    lines,
    lineTotals,
    appliedMainTotal,
    useDualCurrency,
    mainCode,
    secondaryCode,
    amountPaidMain: mainPaid,
    amountPaidSecondary: sec,
    selectedRate,
  };
}

export function PurchaseOrderPrintSheet({
  systemConfig,
  t,
  poId,
  isPreview,
  vendorName,
  reference,
  status,
  poCreatedAt,
  lines,
  lineTotals,
  appliedMainTotal,
  useDualCurrency,
  mainCode,
  secondaryCode,
  amountPaidMain,
  amountPaidSecondary,
  selectedRate,
}) {
  const {
    printCompanyTitle,
    printTagline,
    printHeaderLogo,
    printWatermarkOn,
    printWmOpacity,
    printStampSrc,
    printEmail,
    printWeb,
    printPhoneLines,
    printAddress,
    printFooterNote,
  } = resolveBranding(systemConfig, t);

  const printOutstanding =
    status === "draft" || status === "cancelled"
      ? 0
      : Math.max(0, Math.round((lineTotals - appliedMainTotal) * 100) / 100);

  const printLines = (lines || []).filter((r) => r.product_id != null);

  return (
    <div className="po-print-sheet-wrap" id="purchase-order-print" dir="rtl">
      <div className="po-print-inner">
        {printWatermarkOn ? (
          <img
            src={printHeaderLogo}
            alt=""
            className="po-print-watermark print-watermark"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%) rotate(20deg)",
              opacity: printWmOpacity,
              width: "70%",
              zIndex: 0,
              pointerEvents: "none",
              userSelect: "none",
            }}
          />
        ) : null}
        <div className="po-print-main-column">
          <div className="po-print-top">
            <div className="po-print-header-row">
              <div className="po-print-header-titles">
                <Title level={3} className="po-print-company-title" style={{ margin: 0 }}>
                  {printCompanyTitle}
                </Title>
                {printTagline ? (
                  <Text className="po-print-tagline" style={{ display: "block", marginTop: 4 }}>
                    {printTagline}
                  </Text>
                ) : null}
                <Title level={4} className="po-print-doc-title" style={{ margin: "10px 0 0", fontWeight: 700 }}>
                  {t("poPrintHeading", "Purchase Order")}
                  {!isPreview && poId != null ? ` #${poId}` : ` (${t("poPrintDraftPreview", "Preview")})`}
                </Title>
              </div>
              <div className="po-print-header-logo-wrap">
                {printHeaderLogo ? (
                  <img
                    className="po-print-header-logo"
                    src={printHeaderLogo}
                    alt=""
                    style={{ height: 72, maxWidth: 240, objectFit: "contain" }}
                  />
                ) : null}
              </div>
            </div>

            <table className="po-print-meta-table">
              <tbody>
                <tr>
                  <th>{t("purchaseOrdersFieldVendor", "Vendor")}</th>
                  <td dir="auto">{vendorName}</td>
                </tr>
                <tr>
                  <th>{t("purchaseOrdersFieldReference", "Reference / Document")}</th>
                  <td dir="auto">{reference || "—"}</td>
                </tr>
                <tr>
                  <th>{t("purchaseOrdersColStatus", "Status")}</th>
                  <td>{t(`poStatus_${status}`, status)}</td>
                </tr>
                <tr>
                  <th>{t("date", "Date")}</th>
                  <td dir="ltr">{poCreatedAt ? dayjs(poCreatedAt).format("DD/MM/YYYY HH:mm") : "—"}</td>
                </tr>
              </tbody>
            </table>

            <table className="po-print-lines-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t("purchaseOrdersLineProduct", "Product")}</th>
                  <th>{t("purchaseOrdersLineQty", "Qty")}</th>
                  <th>{t("purchaseOrdersLinePrice", "Unit Price")}</th>
                  <th>{t("purchaseOrdersLineSubtotal", "Line Total")}</th>
                </tr>
              </thead>
              <tbody>
                {printLines.map((row, idx) => (
                  <tr key={row.key}>
                    <td>{idx + 1}</td>
                    <td dir="auto">{row.label || `#${row.product_id}`}</td>
                    <td>{row.quantity}</td>
                    <td>{formatCurrency(toMoney(row.single_price), mainCode)}</td>
                    <td>
                      {formatCurrency(toApiNumber(row.quantity, 0) * toApiNumber(row.single_price, 0), mainCode)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="po-print-bottom-block">
            {/* Same pattern as sale InfoOrder #order-summary: stamp + totals row */}
            <div id="po-order-summary" className="po-print-order-summary">
              {printStampSrc ? (
                <img
                  className="po-print-stamp-img"
                  src={printStampSrc}
                  alt=""
                  style={{ height: 70, width: 70, objectFit: "contain" }}
                />
              ) : (
                <div style={{ width: 70, height: 70 }} />
              )}
              <div className="po-print-summary">
                <p>
                  <b>
                    {formatCurrency(lineTotals, mainCode)} : {t("purchaseOrdersComputedTotal", "Computed Total (from lines)")}{" "}
                  </b>
                </p>
                <p>
                  <b>
                    {formatCurrency(appliedMainTotal, mainCode)} : {t("purchaseOrdersFieldPaid", "Amount Paid")}{" "}
                  </b>
                </p>
                {useDualCurrency && (toMoney(amountPaidSecondary) > 0 || toMoney(amountPaidMain) > 0) ? (
                  <p className="po-print-summary-detail">
                    <b>
                      {mainCode}: {formatCurrency(toMoney(amountPaidMain), mainCode)}
                      {secondaryCode && toMoney(amountPaidSecondary) > 0
                        ? ` · ${secondaryCode}: ${formatCurrency(toMoney(amountPaidSecondary), secondaryCode)}`
                        : ""}
                      {selectedRate
                        ? ` · ${t("exchangeRate", "Exchange rate")}: 1 ${mainCode} = ${selectedRate.main_per_secondary} ${secondaryCode}`
                        : ""}
                    </b>
                  </p>
                ) : null}
                <p>
                  <b>
                    {status === "draft" || status === "cancelled"
                      ? "—"
                      : formatCurrency(printOutstanding, mainCode)}{" "}
                    : {t("purchaseOrdersColOutstanding", "Outstanding")}
                  </b>
                </p>
              </div>
            </div>

            {printFooterNote ? <div className="po-print-footer-note">{printFooterNote}</div> : null}

            {/* Match sale #print-footer: fixed to bottom of each printed page */}
            {(printEmail || printWeb || printPhoneLines.length > 0 || printAddress) ? (
              <div id="po-print-footer" className="po-print-footer-fixed">
                <div>
                  {printEmail ? (
                    <p style={{ margin: 0, fontSize: 14 }}><b>{t("email", "Email")}:{printEmail}</b></p>
                  ) : null}
                  {printWeb ? (
                    <p style={{ margin: 0, fontSize: 14 }}><b>{t("web", "Web")}:{printWeb}</b></p>
                  ) : null}
                </div>
                <div>
                  {printPhoneLines.map((line, i) => (
                    <p key={i} style={{ margin: 0, fontSize: 14 }}>
                      <b>{line}</b>
                    </p>
                  ))}
                </div>
                <div>
                  {printAddress ? (
                    <p style={{ margin: 0, fontSize: 14, fontWeight: "bold", whiteSpace: "pre-wrap" }}>
                      <b>{printAddress}</b>
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
